import { Observable, ReplaySubject } from 'rxjs';

/**
 * Orders overlapping reads of one feed so that the newest read decides. Each
 * read reports a single result: `true` when its response was applied, `false`
 * when it was dropped, or an error.
 *
 * When a newer read has started, a read's own response is not applied and the
 * read reports the newest read's result instead: that read started later, so
 * it reflects at least as much of the server as this one. A cancelled read
 * never answers, so it overtakes nothing: if the read this one waits on is
 * cancelled, the next newest read decides, which can be this read's own
 * answer. Once a read applies, every older read is overtaken by it for good.
 *
 * What a read does with its own answer (apply it, resend, or report a
 * failure) is the owner's decision, made in the callback given to
 * `SequencedRead.whenNewest`.
 */
export class ReadSequencer {
  private sequence = 0;
  private readonly live = new LiveReads();

  /** Starts a read; it is the newest read until a later one starts. */
  start(): SequencedRead {
    const read = new SequencedRead(++this.sequence, this.live);
    this.live.add(read);
    return read;
  }
}

export class SequencedRead {
  private readonly subject = new ReplaySubject<boolean>(1);
  readonly result: Observable<boolean> = this.subject.asObservable();
  private settled = false;
  private cancelled = false;

  constructor(
    readonly sequence: number,
    private readonly live: LiveReads
  ) {
    // Subscribed before anyone else, so the read is settled by the time any
    // other subscriber sees the value and can react to it, for example by
    // cancelling.
    this.subject.subscribe({
      next: () => (this.settled = true),
      error: () => (this.settled = true),
    });
  }

  /**
   * Runs `whenNewest` once no newer live read is left; until then, reports the
   * newest read's result, unless that read is cancelled and so decides nothing.
   */
  whenNewest(whenNewest: () => void): void {
    if (this.settled) {
      return;
    }
    const newest = this.live.newest();
    if (!newest || newest.sequence === this.sequence) {
      whenNewest();
      return;
    }
    newest.result.subscribe({
      next: (applied) => {
        if (!applied && newest.cancelled) {
          this.whenNewest(whenNewest);
        } else if (!this.settled) {
          this.settle(applied);
        }
      },
      error: (error) => {
        if (!this.settled) {
          this.subject.error(error);
        }
      },
    });
  }

  /** Older reads are overtaken by this one for good. */
  overtakeOlderReads(): void {
    this.live.removeOlderThan(this.sequence);
  }

  // Complete in the same step as the value: a subscriber that leaves on the value
  // cancels this read and unsubscribes the request before its own completion runs.
  settle(applied: boolean): void {
    this.subject.next(applied);
    this.subject.complete();
  }

  fail(error: unknown): void {
    this.subject.error(error);
  }

  /** Reports another read's result as this read's own, for example a resend. */
  forward(source: Observable<boolean>): void {
    source.subscribe(this.subject);
  }

  /** Ends a read that has not settled, as dropped. */
  cancel(): void {
    // A settled result keeps its value: overtaken reads may be replaying it.
    if (!this.settled) {
      this.cancelled = true;
      this.live.remove(this);
      this.settle(false);
    }
  }
}

// Reads not cancelled, oldest first; the last one is the newest. A read
// leaves when it is cancelled, or when a newer read applies.
class LiveReads {
  private reads: SequencedRead[] = [];

  add(read: SequencedRead): void {
    this.reads.push(read);
  }

  newest(): SequencedRead | undefined {
    return this.reads[this.reads.length - 1];
  }

  remove(read: SequencedRead): void {
    this.reads = this.reads.filter((live) => live !== read);
  }

  removeOlderThan(sequence: number): void {
    this.reads = this.reads.filter((read) => read.sequence >= sequence);
  }
}
